import { Region } from '@kaizenplatform/hugot-entity';
import { Meta } from '@kaizenplatform/hugot-entity/dist/entity/DynamicSchemaDefinition/IDynamicSchemaSection';
import { Dispatch, SetStateAction, useEffect } from 'react';

import { MaterialTypeOption, materialTypeOrder, SearchAssetResult, SearchAssetService } from '../../entity/SearchAsset';
import { useRecommend } from './useRecommend';
import { useSearchApi } from './useSearchApi';
import { useTypeAndSubType } from './useTypeAndSubType';

export const useSearchAsset = (
  materialTypeOption: MaterialTypeOption,
  meta: Meta,
  region: Region,
  setSelectedSearchAsset: Dispatch<SetStateAction<SearchAssetResult[number] | null>>,
) => {
  const defaultType = materialTypeOrder.find(
    (type) => materialTypeOption[type].enableRecommend || materialTypeOption[type].enableSearch,
  );
  if (defaultType == null) throw new Error();

  const [{ materialType, subType, subTypes }, { handleMaterialTypeChange, handleSubTypeChange }] = useTypeAndSubType({
    defaultType,
    materialTypeOption,
    setSelectedSearchAsset,
  });

  const [{ data, isLoading, isError }, doFetchApi] = useSearchApi({
    type: materialType.name,
    service: (subType.isService ? subType.name : null) as SearchAssetService | null,
    query: '',
    region,
    reset: true,
  });
  const [{ data: recommendData }, doFetchRecommend] = useRecommend([]);

  useEffect(() => {
    if (subType.isService) {
      doFetchApi({
        type: materialType.name,
        service: (subType.isService ? subType.name : null) as SearchAssetService | null,
        query: '',
        region,
        reset: true,
      });
    } else if (subType.isTempo) {
      doFetchApi({
        type: materialType.name,
        service: 'storyblocks',
        query: '',
        region,
        reset: true,
      });
    } else {
      const preparedMaterialCategories = (materialType.name === 'video'
        ? meta.videoPreparedMaterialIds
        : materialType.name === 'image'
        ? meta.imagePreparedMaterialIds
        : materialType.name === 'audio'
        ? meta.audioPreparedMaterialIds
        : []) as ReadonlyArray<number>;
      doFetchRecommend(preparedMaterialCategories);
    }
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialType, subType]);

  return {
    searchApi: {
      searchResult: data,
      isLoading,
      isError,
      doFetchApi,
    },
    typeAndSubType: {
      materialType,
      subType,
      subTypes,
      handleMaterialTypeChange,
      handleSubTypeChange,
    },
    recommend: {
      recommendData,
    },
  } as const;
};
